import { ReactNode, useMemo } from 'react';
import { Box, Typography, Stack, alpha, useTheme, useMediaQuery, keyframes } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import useLocale from 'hooks/useLocale';

const LOGIN_IMAGE = '/assets/images/login/login.png';
const LOGO_IMAGE = '/assets/images/logo/cw-logo.png';

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-14px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.08); opacity: 1; }
`;

const drift = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(12px, -8px); }
  50% { transform: translate(-6px, -16px); }
  75% { transform: translate(-12px, 4px); }
`;

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

interface AuthLoginTemplateProps {
  children: ReactNode;
}

const AuthLoginTemplate = ({ children }: AuthLoginTemplateProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const primary = theme.palette.primary.main;
  const { t } = useLocale();

  const featureCards = useMemo(() => [
    { icon: <BadgeOutlinedIcon />, title: t('auth.featureEmployeeInfo'), desc: t('auth.featureEmployeeInfoDesc') },
    { icon: <CalendarMonthOutlinedIcon />, title: t('auth.featureWorkSchedule'), desc: t('auth.featureWorkScheduleDesc') },
    { icon: <EventBusyOutlinedIcon />, title: t('auth.featureManageLeave'), desc: t('auth.featureManageLeaveDesc') },
    { icon: <AssessmentOutlinedIcon />, title: t('auth.featureSummaryReports'), desc: t('auth.featureSummaryReportsDesc') }
  ], [t]);

  const trustBadges = useMemo(() => [
    { icon: <LockOutlinedIcon sx={{ fontSize: 16 }} />, label: t('auth.trustSecure') },
    { icon: <BoltOutlinedIcon sx={{ fontSize: 16 }} />, label: t('auth.trustFast') },
    { icon: <CloudOutlinedIcon sx={{ fontSize: 16 }} />, label: t('auth.trustCloud') }
  ], [t]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', bgcolor: 'background.default' }}>
      {/* Left: Branding panel */}
      {!isMobile && (
        <Box
          sx={{
            flex: '0 0 50%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            height: '100vh',
            background: `linear-gradient(160deg, ${alpha(primary, 0.03)} 0%, ${alpha(primary, 0.07)} 50%, ${alpha(primary, 0.04)} 100%)`
          }}
        >
          {/* Decorative shapes */}
          <Box sx={{ position: 'absolute', top: -80, right: -80, width: 280, height: 280, borderRadius: '50%', background: alpha(primary, 0.06), animation: `${drift} 20s ease-in-out infinite` }} />
          <Box sx={{ position: 'absolute', bottom: -60, left: -60, width: 220, height: 220, borderRadius: '50%', background: alpha(primary, 0.05), animation: `${drift} 16s ease-in-out infinite reverse` }} />
          <Box sx={{ position: 'absolute', top: '30%', left: '8%', width: 100, height: 100, borderRadius: '50%', border: `2px solid ${alpha(primary, 0.1)}`, animation: `${pulse} 6s ease-in-out infinite` }} />
          <Box sx={{ position: 'absolute', bottom: '20%', right: '10%', width: 60, height: 60, borderRadius: '50%', background: alpha(primary, 0.04), border: `1.5px solid ${alpha(primary, 0.08)}`, animation: `${pulse} 5s ease-in-out infinite 1s` }} />
          <Box sx={{ position: 'absolute', top: '15%', right: '25%', width: 10, height: 10, borderRadius: '50%', background: alpha(primary, 0.2), animation: `${pulse} 3s ease-in-out infinite 0.5s` }} />
          <Box sx={{ position: 'absolute', bottom: '35%', left: '18%', width: 8, height: 8, borderRadius: '50%', background: alpha(primary, 0.15), animation: `${pulse} 4s ease-in-out infinite 2s` }} />

          {/* Content */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: 480,
              width: '100%',
              px: 4,
              py: 3,
              animation: `${fadeSlideUp} 0.8s ease-out`
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <img src={LOGO_IMAGE} alt="Logo" width={44} />
              <Typography
                variant="h3"
                fontWeight={700}
                sx={{
                  letterSpacing: '-0.01em',
                  background: `linear-gradient(135deg, ${primary} 0%, ${theme.palette.primary.dark} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Cloudwork
              </Typography>
            </Stack>

            <Box
              component="img"
              src={LOGIN_IMAGE}
              alt="Login illustration"
              sx={{
                width: '100%',
                maxWidth: 260,
                height: 'auto',
                objectFit: 'contain',
                mb: 3,
                animation: `${float} 6s ease-in-out infinite`,
                filter: `drop-shadow(0 16px 32px ${alpha(primary, 0.15)})`
              }}
            />

            <Stack spacing={0.75} alignItems="center" sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" fontWeight={700} color="text.primary" sx={{ letterSpacing: '-0.02em' }}>
                {t('auth.welcomeBack')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 340 }}>
                {t('auth.welcomeSubtitle')}
              </Typography>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, width: '100%' }}>
              {featureCards.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.paper, 0.7),
                    border: `1px solid ${alpha(primary, 0.08)}`,
                    backdropFilter: 'blur(8px)',
                    transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
                    '&:hover': {
                      borderColor: alpha(primary, 0.2),
                      boxShadow: `0 4px 16px ${alpha(primary, 0.08)}`
                    }
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 0.5, '& .MuiSvgIcon-root': { fontSize: 20 } }}>
                    {item.icon}
                  </Box>
                  <Typography variant="caption" fontWeight={600} color="text.primary" sx={{ display: 'block', mb: 0.25 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
                    {item.desc}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* Right: Form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 3, sm: 4, md: 5 },
          py: { xs: 4, md: 0 },
          position: 'relative',
          overflow: 'hidden',
          borderLeft: { md: `1px solid ${theme.palette.divider}` },
          backgroundImage: { md: `radial-gradient(${alpha(primary, 0.045)} 1px, transparent 1px)` },
          backgroundSize: '24px 24px'
        }}
      >
        {!isMobile && (
          <>
            <Box sx={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: alpha(primary, 0.04), animation: `${pulse} 8s ease-in-out infinite` }} />
            <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 100, height: 100, borderRadius: '50%', border: `1.5px solid ${alpha(primary, 0.08)}`, animation: `${pulse} 7s ease-in-out infinite 2s` }} />
          </>
        )}

        {isMobile && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
            <img src={LOGO_IMAGE} alt="Logo" width={40} />
            <Typography variant="h4" fontWeight={700} color="primary.main">
              Cloudwork
            </Typography>
          </Stack>
        )}

        <Box
          sx={{
            width: '100%',
            maxWidth: 460,
            position: 'relative',
            zIndex: 1,
            animation: `${fadeSlideUp} 0.7s ease-out 0.15s both`
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              p: { xs: 3, sm: 4 },
              boxShadow: `0 4px 24px ${alpha(primary, 0.06)}`
            }}
          >
            {children}
          </Box>

          <Stack
            direction="row"
            justifyContent="center"
            spacing={3}
            sx={{ mt: 3, animation: `${fadeSlideUp} 0.7s ease-out 0.45s both` }}
          >
            {trustBadges.map((item) => (
              <Stack key={item.label} direction="row" alignItems="center" spacing={0.75}>
                <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                  {item.icon}
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  {item.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            position: { md: 'absolute' },
            bottom: { md: 24 },
            mt: { xs: 4, md: 0 },
            animation: `${fadeSlideUp} 0.7s ease-out 0.6s both`
          }}
        >
          &copy; {new Date().getFullYear()} Cloudwork. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default AuthLoginTemplate;
